import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Divider, IconButton, List, ListItemButton, ListItemIcon, ListItemText, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { MenuGroup } from '../../constants/menuItems';

export const SIDE_NAV_WIDTH = 240;
export const SIDE_NAV_RAIL_WIDTH = 64;

interface SideNavProps {
  groups: MenuGroup[];
  isActive: (path: string) => boolean;
  /** Icons only; labels become tooltips. Ignored in the mobile drawer. */
  collapsed?: boolean;
  /** Shows the collapse/expand control (desktop sidebar only). */
  onToggleCollapsed?: () => void;
  /** Called after a page was chosen, e.g. to close the mobile drawer. */
  onNavigate?: () => void;
}

const SideNav: React.FC<SideNavProps> = ({ groups, isActive, collapsed = false, onToggleCollapsed, onNavigate }) => (
  <Box component="nav" aria-label="Main navigation" sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
    <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', pb: 1 }}>
      {groups.map((group, index) => (
        <List
          key={group.id}
          disablePadding
          aria-label={group.label}
          subheader={
            collapsed ? (
              index > 0 ? <Divider sx={{ mx: 1.5, my: 1 }} /> : <Box sx={{ height: 8 }} />
            ) : (
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ display: 'block', px: 3, pt: index === 0 ? 1.5 : 2, pb: 0.5, lineHeight: 1.6, letterSpacing: '0.08em', whiteSpace: 'nowrap' }}
              >
                {group.label}
              </Typography>
            )
          }
        >
          {group.items.map((item) => {
            const active = isActive(item.path);
            const button = (
              <ListItemButton
                component={Link}
                to={item.path}
                onClick={onNavigate}
                selected={active}
                aria-current={active ? 'page' : undefined}
                aria-label={collapsed ? item.label : undefined}
                sx={(theme) => ({
                  mx: 1,
                  my: 0.25,
                  px: collapsed ? 0 : 1.5,
                  py: 1.1,
                  borderRadius: 1,
                  justifyContent: collapsed ? 'center' : 'flex-start',
                  color: active ? 'primary.main' : 'text.primary',
                  '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.14) },
                })}
              >
                <ListItemIcon sx={{ minWidth: collapsed ? 0 : 40, justifyContent: 'center', color: active ? 'primary.main' : 'text.secondary' }}>
                  <item.icon />
                </ListItemIcon>
                {!collapsed && <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: active ? 600 : 400, noWrap: true }} />}
              </ListItemButton>
            );
            return collapsed ? (
              <Tooltip key={item.path} title={item.label} placement="right" arrow>
                {button}
              </Tooltip>
            ) : (
              <React.Fragment key={item.path}>{button}</React.Fragment>
            );
          })}
        </List>
      ))}
    </Box>
    {onToggleCollapsed && (
      <Box sx={{ borderTop: 1, borderColor: 'divider', p: 1, display: 'flex', justifyContent: collapsed ? 'center' : 'flex-end' }}>
        <Tooltip title={collapsed ? 'Expand menu' : 'Collapse menu'} placement="right">
          <IconButton onClick={onToggleCollapsed} aria-label={collapsed ? 'Expand menu' : 'Collapse menu'} size="small">
            {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Tooltip>
      </Box>
    )}
  </Box>
);

export default SideNav;
